import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Eye, EyeOff, Play, Save } from 'lucide-react';
import patientCallingService, { CallSchedule } from '../../../services/patientCalling.service';
import CommonDropdown from '../../Common/CommonDropdown';
import styles from './ScheduleCallDrawer.module.css';

const ENTITY_TYPE_OPTIONS = [
    { value: 'Patient', label: 'Patient' },
    { value: 'Facility', label: 'Facility' },
];

const LLM_PROVIDER_OPTIONS = [
    { value: 'Groq', label: 'Groq' },
    { value: 'OpenAI', label: 'OpenAI' },
];

const TTS_PROVIDER_OPTIONS = [
    { value: 'ElevenLabs', label: 'ElevenLabs' },
];

interface Props {
    selectedServiceLogIds: string[];
    batchName: string;
    totalRecords: number;
    onClose: () => void;
    onScheduled: () => void;
}

const CRON_PRESETS = [
    { label: 'Daily 9am', value: '0 9 * * *' },
    { label: 'Mon 9am', value: '0 9 * * 1' },
    { label: 'Bi-weekly Mon', value: '0 9 * * 1/2' },
];

const ScheduleCallDrawer: React.FC<Props> = ({ selectedServiceLogIds, batchName, totalRecords, onClose, onScheduled }) => {
    const [form, setForm] = useState<Record<string, any>>({
        schedule_type: 'one_time',
        extra_parameters: [] as { key: string; value: string }[],
        is_active: true,
    });
    const [showKey, setShowKey] = useState(false);
    const [loading, setLoading] = useState(false);
    const [triggering, setTriggering] = useState(false);
    const [error, setError] = useState('');
    const [existingSchedules, setExistingSchedules] = useState<CallSchedule[]>([]);

    const [config, setConfig] = useState<any>(null);

    useEffect(() => {
    patientCallingService.getConfig()
        .then(setConfig)
        .catch(() => {});
    }, []);
    useEffect(() => {
    patientCallingService
        .listSchedules()
        .then(setExistingSchedules)
        .catch(() => {});
}, []);

    const set = (field: string, val: any) => setForm(prev => ({ ...prev, [field]: val }));

    const addParam = () => set('extra_parameters', [...(form.extra_parameters || []), { key: '', value: '' }]);
    const removeParam = (i: number) => set('extra_parameters', form.extra_parameters.filter((_: any, idx: number) => idx !== i));
    const setParam = (i: number, field: 'key' | 'value', val: string) => {
        const updated = [...form.extra_parameters];
        updated[i] = { ...updated[i], [field]: val };
        set('extra_parameters', updated);
    };

    const buildPayload = () => {
        const extraParams: Record<string, string> = {};

        (form.extra_parameters || []).forEach((p: any) => {
            if (p.key) extraParams[p.key] = p.value;
        });

        return {
            calling_system_call_type: form.calling_system_call_type,
            calling_system_entity_type: form.calling_system_entity_type || "Patient",

            // ✅ FROM CONFIG
            llm_provider: config?.llm_provider,
            tts_provider: config?.tts_provider,
            phone_country_code: config?.phone_country_code,

            // ❌ REMOVED
            // ivr_menu: ❌

            extra_parameters: Object.keys(extraParams).length
            ? extraParams
            : undefined,

            schedule_type: form.schedule_type,
            scheduled_at:
            form.schedule_type === "one_time"
                ? form.scheduled_at
                : undefined,

            cron_expression:
            form.schedule_type === "recurring"
                ? form.cron_expression
                : undefined,

            service_log_ids: selectedServiceLogIds,
        };
        };

    const samplePayload = () => ({
  call_type: form.calling_system_call_type || "Payment Reminder",
  entity_type: form.calling_system_entity_type || "Patient",

  phone_number: `${config?.phone_country_code || "+1"}5550001234`,

  llm_provider: config?.llm_provider,
  tts_provider: config?.tts_provider,

  parameters: {
    patient_name: "SMITH, JOHN",
    date_of_service: "03/15/2026",
    total_balance: "150.00",
    ...(form.extra_parameters || []).reduce((acc: any, p: any) => {
      if (p.key) acc[p.key] = p.value;
      return acc;
    }, {}),
  },
});

    const validate = () => {
        if (!form.calling_system_call_type?.trim())
            return "Call type is required";

        if (form.schedule_type === "one_time" && !form.scheduled_at)
            return "Please select a date and time";

        if (form.schedule_type === "recurring" && !form.cron_expression?.trim())
            return "Please enter or select a cron expression";

        return null;
        };
    const handleSave = async () => {
        const err = validate();
        if (err) { setError(err); return; }
        setLoading(true); setError('');
        try {
            await patientCallingService.createSchedule(buildPayload());
            onScheduled();
        } catch (e: any) { setError(e.message || 'Failed to create schedule'); }
        finally { setLoading(false); }
    };

    const handleSaveAndTrigger = async () => {
        const err = validate();
        if (err) { setError(err); return; }
        setTriggering(true); setError('');
        try {
            const schedule = await patientCallingService.createSchedule(buildPayload());
            await patientCallingService.triggerSchedule(schedule.id);
            alert(`Dispatch started! ${totalRecords} calls will be sent one by one.`);
            onScheduled();
        } catch (e: any) { setError(e.message || 'Failed to trigger'); }
        finally { setTriggering(false); }
    };

    const handleTriggerExisting = async (scheduleId: string) => {
        try {
            await patientCallingService.triggerSchedule(scheduleId);
            alert('Dispatch started!');
        } catch { alert('Failed to trigger'); }
    };

    return (
        <div className={styles.drawerOverlay} onClick={onClose}>
            <div className={styles.drawerWide} onClick={e => e.stopPropagation()}>
                <div className={styles.drawerHeader}>
                    <div>
                        <h2 className={styles.drawerTitle}>Schedule Call Campaign</h2>
                        <p className={styles.mutedText}>{batchName} — {totalRecords} patients</p>
                    </div>
                    <button className={styles.closeBtn} onClick={onClose}><X size={18} /></button>
                </div>

                <div className={styles.drawerBodySplit}>
                    {/* LEFT: Form */}
                    <div className={styles.drawerFormPanel}>
                        {error && <div className={styles.errorBanner}>{error}</div>}

                        {/* Existing schedules */}
                        {existingSchedules.length > 0 && (
                            <div className={styles.formSection}>
                                <div className={styles.sectionLabel}>Existing Schedules</div>
                                {existingSchedules.map(s => (
                                    <div key={s.id} className={styles.existingScheduleRow}>
                                        <div>
                                            <span className={styles.boldCell}>{s.schedule_type === 'one_time' ? `One-time: ${s.scheduled_at ? new Date(s.scheduled_at).toLocaleString() : '—'}` : `Recurring: ${s.cron_expression}`}</span>
                                            <span className={styles.mutedText}> · {s.status}</span>
                                        </div>
                                        <button className={styles.iconBtn} title="Trigger now" onClick={() => handleTriggerExisting(s.id)}>
                                            <Play size={14} />
                                        </button>
                                    </div>
                                ))}
                                <div className={styles.divider} />
                                <div className={styles.sectionLabel}>Create New Schedule</div>
                            </div>
                        )}

                        {/* Calling System Config */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionLabel}>Calling System Config</div>
                            <div className={styles.formGrid2}>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Call Type</label>
                                    <input className={styles.input} placeholder="e.g. Payment Reminder" value={form.calling_system_call_type || ''} onChange={e => set('calling_system_call_type', e.target.value)} />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Entity Type</label>
                                    <CommonDropdown
                                        value={form.calling_system_entity_type || 'Patient'}
                                        onChange={(v) => set('calling_system_entity_type', v || 'Patient')}
                                        options={ENTITY_TYPE_OPTIONS}
                                        placeholder="Select entity type"
                                        size="md"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Schedule */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionLabel}>Schedule</div>
                            <div className={styles.toggleRow}>
                                <button className={`${styles.toggleBtn} ${form.schedule_type === 'one_time' ? styles.toggleBtnActive : ''}`} onClick={() => set('schedule_type', 'one_time')}>One-Time</button>
                                <button className={`${styles.toggleBtn} ${form.schedule_type === 'recurring' ? styles.toggleBtnActive : ''}`} onClick={() => set('schedule_type', 'recurring')}>Recurring</button>
                            </div>
                            {form.schedule_type === 'one_time' ? (
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Date & Time <span className={styles.required}>*</span></label>
                                    <input type="datetime-local" className={styles.input} value={form.scheduled_at || ''} onChange={e => set('scheduled_at', e.target.value)} />
                                </div>
                            ) : (
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Cron Schedule <span className={styles.required}>*</span></label>
                                    <div className={styles.presetRow}>
                                        {CRON_PRESETS.map(p => (
                                            <button key={p.value} type="button"
                                                className={`${styles.presetBtn} ${form.cron_expression === p.value ? styles.presetBtnActive : ''}`}
                                                onClick={() => set('cron_expression', p.value)}>{p.label}</button>
                                        ))}
                                    </div>
                                    <input className={styles.input} placeholder="Custom: 0 9 * * 1" value={form.cron_expression || ''} onChange={e => set('cron_expression', e.target.value)} />
                                </div>
                            )}
                        </div>

                        {/* Extra Parameters */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionLabelRow}>
                                <span className={styles.sectionLabel}>Extra Parameters</span>
                                <button type="button" className={styles.smallLinkBtn} onClick={addParam}><Plus size={13} /> Add</button>
                            </div>
                            <p className={styles.hintText}>Static values merged into every call's parameters block</p>
                            {(form.extra_parameters || []).map((p: any, i: number) => (
                                <div key={i} className={styles.paramRow}>
                                    <input className={styles.input} placeholder="Key" value={p.key} onChange={e => setParam(i, 'key', e.target.value)} />
                                    <input className={styles.input} placeholder="Value" value={p.value} onChange={e => setParam(i, 'value', e.target.value)} />
                                    <button type="button" className={styles.iconBtnDanger} onClick={() => removeParam(i)}><Trash2 size={14} /></button>
                                </div>
                            ))}
                        </div>

                        {/* IVR Menu */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionLabelRow}>
                                <span className={styles.sectionLabel}>IVR Menu</span>
                                <label className={styles.checkboxLabel}>
                                    <input type="checkbox" checked={!!form.ivr_menu_enabled} onChange={e => set('ivr_menu_enabled', e.target.checked)} />
                                    Enable IVR
                                </label>
                            </div>
                            {form.ivr_menu_enabled && (
                                <textarea
                                    className={styles.textarea}
                                    rows={4}
                                    value={form.ivr_menu_json || ''}
                                    onChange={e => set('ivr_menu_json', e.target.value)}
                                    placeholder={`[{"intent":"Support","response":"1","triggers":["press 1","support"]}]`}
                                />
                            )}
                        </div>
                    </div>

                    {/* RIGHT: Payload Preview */}
                    <div className={styles.previewPanel}>
                        <div className={styles.sectionLabel}>Sample Payload Preview</div>
                        <p className={styles.hintText}>This is what will be sent to the calling system for each patient</p>
                        <pre className={styles.jsonPreview}>
                            {JSON.stringify(samplePayload(), null, 2)}
                        </pre>
                    </div>
                </div>

                <div className={styles.drawerFooter}>
                    <button type="button" className={styles.outlineBtn} onClick={onClose}>Cancel</button>
                    <button type="button" className={styles.outlineBtn} onClick={handleSave} disabled={loading}>
                        <Save size={15} /> {loading ? 'Saving...' : 'Save Schedule'}
                    </button>
                    <button type="button" className={styles.primaryBtn} onClick={handleSaveAndTrigger} disabled={triggering}>
                        <Play size={15} /> {triggering ? 'Triggering...' : 'Save & Trigger Now'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ScheduleCallDrawer;
